import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/app/DataTable";
import { StatusBadge } from "@/components/app/common";
import { EntityPicker } from "@/components/app/EntityPicker";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, inr } from "@/lib/format";

const BASE = [
  { key: "order_no", label: "LM No.", mono: true }, { key: "customer", label: "Customer", wrap: true }, { key: "lm_date", label: "LM date", render: (r) => fmtDate(r.lm_date) },
  { key: "value", label: "Value", right: true, render: (r) => inr(r.value) },
  { key: "restore_to", label: "Status before cancellation", render: (r) => (r.restore_to ? <StatusBadge status={r.restore_to} /> : "—") },
  { key: "cancelled_by", label: "Cancelled by", value: (r) => r.cancelled_by?.name, exp: (r) => r.cancelled_by?.name || "", render: (r) => r.cancelled_by?.name || "—" },
  { key: "cancelled_at", label: "Cancelled at", render: (r) => fmtDateTime(r.cancelled_at) }, { key: "cancel_reason", label: "Reason", wrap: true },
  { key: "warnings", label: "Warnings", wrap: true, exp: (r) => (r.warnings || []).join("; "), render: (r) => <span className="text-xs text-amber-800">{(r.warnings || []).join("; ") || "—"}</span> },
];
const RESULT = [...BASE.slice(0, 2), BASE[4], { key: "result", label: "Result", render: (r) => <StatusBadge status={r.result} /> }, { key: "message", label: "Message", wrap: true }, BASE[8]];

function Filters({ f, setF, users }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3" data-testid="revive-filters">
      <div><Label className="text-xs">Cancelled from</Label><Input type="date" value={f.date_from} onChange={set("date_from")} data-testid="revive-filter-from" /></div>
      <div><Label className="text-xs">Cancelled to</Label><Input type="date" value={f.date_to} onChange={set("date_to")} data-testid="revive-filter-to" /></div>
      <div><Label className="text-xs">Cancelled by</Label>
        <select className="h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm" value={f.cancelled_by} onChange={set("cancelled_by")} data-testid="revive-filter-by">
          <option value="">Anyone</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select></div>
      <div><Label className="text-xs">Customer</Label><EntityPicker url="/customers" value={f.customer} onChange={(c) => setF({ ...f, customer: c, customer_id: c?.id || "" })} render={(c) => ({ title: c.name, subtitle: c.code })} placeholder="All customers" testid="revive-filter-customer" /></div>
      <div><Label className="text-xs">LM number</Label><Input value={f.q} onChange={set("q")} data-testid="revive-filter-q" /></div>
      <div><Label className="text-xs">Reason contains</Label><Input value={f.reason} onChange={set("reason")} data-testid="revive-filter-reason" /></div>
    </div>
  );
}

// LM Register: preview + revive the ticked cancelled LMs (same backend as the Admin screen)
export function BulkReviveDialog({ ids, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (commit) => {
    if (reason.trim().length < 3) return toast.error("Enter the reason for reviving (at least 3 characters)");
    setBusy(true);
    try {
      const r = await api.post(`/admin/revive-lms?commit=${commit}`, { ids, reason });
      setOut(r.data);
      if (commit) { toast.success(`${r.data.revived} revived · ${r.data.skipped} skipped`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="bulk-revive-dialog">
        <DialogHeader><DialogTitle>Revive {ids.length} cancelled LM(s)</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Reason for reviving (applies to all) *</Label><Input value={reason} onChange={(e) => { setReason(e.target.value); if (!out?.committed) setOut(null); }} placeholder="Restoring LMs cancelled by mistake on …" data-testid="bulk-revive-reason" /></div>
        {out && <>
          <p className="text-sm font-semibold" data-testid="bulk-revive-summary">{out.committed ? `Result: ${out.revived} revived, ${out.skipped} skipped` : `Preview - nothing changed yet (${out.results.length - out.skipped} will be revived, ${out.skipped} skipped)`}</p>
          <DataTable testid="bulk-revive-table" columns={RESULT} rows={out.results} exportName={out.committed ? "LM_Revive_Result" : "LM_Revive_Preview"} exportPerm="orders.export" />
        </>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="bulk-revive-close">{out?.committed ? "Close" : "Cancel"}</Button>
          {!out?.committed && <Button variant="outline" disabled={busy} onClick={() => run(false)} data-testid="bulk-revive-preview-btn">Preview revive</Button>}
          {!out?.committed && <Button disabled={busy || !out} onClick={() => run(true)} data-testid="bulk-revive-commit-btn">Revive selected</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Admin > Revive cancelled LMs: filter, tick, preview, commit
export function ReviveLmsPanel() {
  const { me } = useMe();
  const [f, setF] = useState({ date_from: "", date_to: "", cancelled_by: "", customer_id: "", customer: null, q: "", reason: "" });
  const [tick, setTick] = useState(0);
  const qs = new URLSearchParams(Object.entries(f).filter(([k, v]) => v && k !== "customer")).toString();
  const { data } = useFetch(me.roles.includes("admin") ? `/admin/revive-lms?${qs}&t=${tick}` : null);
  const [sel, setSel] = useState(new Set());
  const [reason, setReason] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => data?.rows || [], [data]);
  if (!me.roles.includes("admin")) return <p className="text-sm text-slate-500" data-testid="revive-admin-only">Only an Admin can revive cancelled LMs.</p>;
  const run = async (commit) => {
    if (reason.trim().length < 3) return toast.error("Enter the reason for reviving (at least 3 characters)");
    setBusy(true);
    try {
      const r = await api.post(`/admin/revive-lms?commit=${commit}`, { ids: [...sel], reason });
      setOut(r.data);
      if (commit) { toast.success(`${r.data.revived} revived · ${r.data.skipped} skipped`); setSel(new Set()); setTick((t) => t + 1); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="revive-lms-panel">
      <Filters f={f} setF={(x) => { setF(x); setSel(new Set()); setOut(null); }} users={data?.users || []} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[240px]"><Label className="text-xs">Reason for reviving (applies to the whole batch) *</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Restoring LMs cancelled by mistake on …" data-testid="revive-batch-reason" /></div>
        <Button variant="outline" disabled={!rows.length} onClick={() => setSel(new Set(rows.map((r) => r.id)))} data-testid="revive-select-all">Select all filtered ({rows.length})</Button>
        <Button variant="outline" disabled={busy || !sel.size} onClick={() => run(false)} data-testid="revive-preview-btn">Preview revive ({sel.size})</Button>
        <Button disabled={busy || !sel.size || !out || out.committed} onClick={() => run(true)} data-testid="revive-commit-btn">Revive selected</Button>
      </div>
      {out && <div className="mb-4" data-testid="revive-result">
        <p className="mb-1 text-sm font-semibold">{out.committed ? `Result: ${out.revived} revived, ${out.skipped} skipped` : `Preview - nothing changed yet (${out.results.length - out.skipped} will be revived, ${out.skipped} skipped)`}</p>
        <DataTable testid="revive-result-table" columns={RESULT} rows={out.results} exportName={out.committed ? "LM_Revive_Result" : "LM_Revive_Preview"} exportPerm="orders.export" />
      </div>}
      <DataTable testid="revive-table" columns={BASE} rows={data ? rows : null} selected={sel} onSelect={(s) => { setSel(s); setOut(null); }} exportName="Cancelled_LMs" exportPerm="orders.export" empty="No cancelled LMs for these filters" />
    </div>
  );
}
